import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Mail, Phone, MapPin, Clock, MessageCircle,
  Instagram, ChevronDown, Send, Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// ── Intersection observer hook ────────────────────────────────────────────────

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("section-visible");
          el.classList.remove("section-hidden");
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// ── FAQ data ──────────────────────────────────────────────────────────────────

const FAQS = [
  {
    icon: "💎",
    q: "Is your jewellery genuine 92.5 sterling silver?",
    a: "Yes. Every Rivora piece is crafted using genuine 92.5 sterling silver — the international standard for sterling silver purity. Products meeting the applicable BIS weight threshold are hallmarked in accordance with Indian regulations. Every piece is quality-checked before dispatch, so you can wear it with complete confidence.",
  },
  {
    icon: "🚚",
    q: "How long does shipping take?",
    a: "Orders are processed within 1–2 business days and typically delivered within 5–7 business days across India. You'll receive a tracking link via email and SMS as soon as your order is shipped. You can also check real-time status under My Account → Orders.",
  },
  {
    icon: "💳",
    q: "What payment methods do you accept?",
    a: "We accept all major payment methods including UPI, Debit/Credit Cards (Visa, Mastercard), Net Banking, and Google Pay via Razorpay. All transactions are secured with industry-standard encryption. Please note that Cash on Delivery (COD) is not available at this time.",
  },
  {
    icon: "↩",
    q: "What is your return and refund policy?",
    a: "We accept returns only if you receive a damaged product or an incorrect item. A clear, continuous, unedited unboxing video is mandatory to process any claim — the package must be unopened at the start of the video and recorded in a single take. Claims must be reported within 24 hours of delivery via WhatsApp or email. Once verified, we will arrange a replacement or issue a full refund within 5–7 business days. We do not accept returns for change of mind, incorrect size selected by the customer, or personalised jewellery.",
  },
  {
    icon: "📦",
    q: "How do I track my order?",
    a: "Once your order is shipped, you'll receive a tracking link via email and SMS.",
  },
  {
    icon: "🌸",
    q: "Is Rivora jewellery safe for sensitive skin?",
    a: "Absolutely. 92.5 sterling silver is naturally hypoallergenic and skin-friendly. Our pieces are free from nickel and harmful alloys, making them safe for daily wear — even for those with sensitive skin. If you experience any unusual reaction, please discontinue use and contact us.",
  },
  {
    icon: "✨",
    q: "Can I wear Rivora jewellery every day?",
    a: "Yes — Rivora jewellery is designed for everyday elegance. Sterling silver is durable and built to last. To keep your pieces looking their best, we recommend removing them before swimming, showering, exercising, or applying perfumes and chemicals. A gentle wipe with a soft cloth after wearing will help maintain their shine.",
  },
  {
    icon: "🎁",
    q: "Do you provide gift packaging?",
    a: "Yes. Every Rivora order is packed with care in premium gift-ready packaging — perfect for gifting a loved one or treating yourself. No additional charge is applied. If you'd like a personalised note included, please mention it in the order notes at checkout.",
  },
];

// ── FAQ accordion item ────────────────────────────────────────────────────────

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-zinc-100 last:border-0"
      data-testid={`faq-${index}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-5 text-left gap-4 group"
        aria-expanded={open}
      >
        <span className="flex items-center gap-3 flex-1 min-w-0">
          {faq.icon && <span className="text-base flex-shrink-0">{faq.icon}</span>}
          <span className={`text-sm font-medium transition-colors duration-200 ${open ? "text-zinc-900" : "text-zinc-700 group-hover:text-zinc-900"}`}>
            {faq.q}
          </span>
        </span>
        <span className="flex-shrink-0 transition-transform duration-250 ease-out" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <ChevronDown size={16} className="text-[#C9A96E]" />
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-250 ease-out"
        style={{ maxHeight: open ? "400px" : "0px", opacity: open ? 1 : 0, transition: "max-height 250ms ease-out, opacity 250ms ease-out" }}
      >
        <p className="text-sm text-zinc-500 leading-[1.85] pb-5 pr-6">
          {faq.a}
        </p>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const DEFAULT_CONTACT = {
  email: "hello@rivorajewels.com",
  phone: "+91 99999 99999",
  whatsapp: "919999999999",
  address: "Johri Bazaar, Jaipur\nRajasthan – 302003, India"
};

const DEFAULT_SOCIAL = {
  instagram: "https://instagram.com/rivorajewels",
  facebook: "https://facebook.com",
  gmail: "hello@rivorajewels.com"
};

export default function ContactPage() {
  const contactRef = useSectionReveal();
  const faqRef     = useSectionReveal();

  const [contactInfo, setContactInfo] = useState(DEFAULT_CONTACT);
  const [socialInfo, setSocialInfo]   = useState(DEFAULT_SOCIAL);

  // Scroll to #faq after page renders if hash is present
  useEffect(() => {
    if (window.location.hash === "#faq") {
      const el = document.getElementById("faq");
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/admin/content/contact`)
      .then(({ data }) => {
        if (data.content && Object.keys(data.content).length > 0) {
          setContactInfo({ ...DEFAULT_CONTACT, ...data.content });
        }
      })
      .catch(() => {});

    axios.get(`${API}/admin/content/social`)
      .then(({ data }) => {
        if (data.content && Object.keys(data.content).length > 0) {
          setSocialInfo({ ...DEFAULT_SOCIAL, ...data.content });
        }
      })
      .catch(() => {});
  }, []);

  const [form, setForm]         = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try emailing us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Navbar />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="bg-zinc-900 py-16 sm:py-24 text-center relative overflow-hidden">
        {/* Radial spotlight behind heading */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 70% 60% at 50% 80%, rgba(212,175,55,0.12) 0%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        {/* Fine grain texture overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")",
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 px-6 md:px-12 max-w-3xl mx-auto">
          {/* Label */}
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">
            Always Here to Help
          </p>

          {/* Headline */}
          <h1
            className="cormorant text-5xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight mb-5"
            data-testid="contact-title"
          >
            We're Here for<br className="hidden sm:block" /> Every Question
          </h1>

          {/* Gold divider */}
          <div className="w-12 h-0.5 bg-[#D4AF37] mx-auto mb-6" aria-hidden="true" />

          {/* Subtext */}
          <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-8 max-w-xl mx-auto">
            Whether you need order assistance, or product support — our team is here to help.
          </p>

          {/* Trust line */}
          <p className="text-[#D4AF37]/70 text-xs tracking-widest uppercase mb-10">
            Response within 30 minutes&nbsp;&nbsp;·&nbsp;&nbsp;Mon–Fri&nbsp;&nbsp;·&nbsp;&nbsp;10 AM – 7 PM
          </p>

          {/* Trust icons row */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              ), label: "WhatsApp Support" },
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              ), label: "Email Support" },
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2"/><path d="M12 6v6l4 2"/></svg>
              ), label: "Order Assistance" },
              { icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 3h12l4 6-10 13L2 9z"/><path d="M2 9h20M6 3l-4 6M18 3l4 6"/></svg>
              ), label: "Product Queries" },
            ].map(({ icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <span className="text-[#D4AF37]/80">{icon}</span>
                <span className="text-[10px] uppercase tracking-widest text-white/40">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHATSAPP CTA STRIP — premium ivory style ─────────────────── */}
      <div className="bg-[#F5F0E8] border-b border-[#E8DFC8] py-4 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-center sm:text-left">
          <div className="flex items-center gap-2 text-zinc-700">
            <MessageCircle size={18} className="text-[#1D7F5B]" />
            <span className="text-sm font-medium">
              Fastest response on WhatsApp — usually within minutes
            </span>
          </div>
          <a
            href={`https://wa.me/${contactInfo.whatsapp}?text=Hi%20Rivora!%20I%20have%20a%20question.`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 bg-[#1D7F5B] text-white font-medium px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#166647] transition-colors rounded-sm"
            data-testid="whatsapp-cta"
          >
            Chat Now
          </a>
        </div>
      </div>

      {/* ── CONTACT INFO + FORM ───────────────────────────────────────── */}
      <section
        ref={contactRef}
        className="py-16 sm:py-24 section-hidden"
        data-testid="contact-section"
      >
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

            {/* Left — contact details ──────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-10">

              {/* Get in touch */}
              <div>
                <h2 className="cormorant text-3xl font-light text-zinc-900 mb-6">
                  Let's Connect
                </h2>
                <div className="space-y-5">

                  {/* Email — clickable */}
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-start gap-4 group"
                    data-testid="contact-email"
                  >
                    <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37] transition-colors">
                      <Mail size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-400 mb-0.5">Email</p>
                      <p className="text-sm text-zinc-700 group-hover:text-zinc-900 transition-colors">
                        {contactInfo.email}
                      </p>
                    </div>
                  </a>

                  {/* Phone — clickable */}
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="flex items-start gap-4 group"
                    data-testid="contact-phone"
                  >
                    <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37] transition-colors">
                      <Phone size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-400 mb-0.5">Phone</p>
                      <p className="text-sm text-zinc-700 group-hover:text-zinc-900 transition-colors">
                        {contactInfo.phone}
                      </p>
                    </div>
                  </a>

                  {/* Address */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center flex-shrink-0">
                      <MapPin size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-400 mb-0.5">Address</p>
                      <p className="text-sm text-zinc-700 leading-relaxed">
                        {contactInfo.address.split('\n').map((line, i) => (
                          <span key={i}>{line}<br /></span>
                        ))}
                      </p>
                    </div>
                  </div>

                  {/* Business hours */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center flex-shrink-0">
                      <Clock size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Business Hours</p>
                      <div className="text-sm text-zinc-700 space-y-0.5">
                        <p>Mon – Fri: 10 AM – 7 PM</p>
                        <p>Saturday: 10 AM – 5 PM</p>
                        <p className="text-zinc-400">Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response time note */}
              <div className="bg-white border border-zinc-100 p-4">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  <span className="text-zinc-800 font-medium">⚡ Typical response time:</span>{" "}
                  WhatsApp — within minutes. Email — within 24 hours on business days.
                </p>
              </div>

              {/* Social links */}
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-400 mb-4">
                  Follow Us
                </p>
                <div className="flex gap-3">
                  <a
                    href={socialInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-zinc-200 hover:border-[#D4AF37] flex items-center justify-center transition-colors group"
                    aria-label="Instagram"
                    data-testid="instagram-link"
                  >
                    <Instagram size={16} className="text-zinc-500 group-hover:text-[#D4AF37] transition-colors" />
                  </a>
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-zinc-200 hover:border-[#25D366] flex items-center justify-center transition-colors group"
                    aria-label="WhatsApp"
                  >
                    <MessageCircle size={16} className="text-zinc-500 group-hover:text-[#25D366] transition-colors" />
                  </a>
                </div>
              </div>
            </div>

            {/* Right — contact form ─────────────────────────────────────── */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-zinc-100 p-6 md:p-8 shadow-sm">
                <h2 className="cormorant text-3xl font-light text-zinc-900 mb-2">
                  Send a Message
                </h2>
                <p className="text-sm text-zinc-500 mb-8">
                  Fill in the form and we'll get back to you as soon as possible.
                </p>

                {submitted ? (
                  /* Success state */
                  <div className="text-center py-12" data-testid="form-success">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check size={24} className="text-green-600" />
                    </div>
                    <p className="cormorant text-2xl font-light text-zinc-900 mb-2">
                      Message Sent!
                    </p>
                    <p className="text-sm text-zinc-500 mb-6">
                      Thank you for reaching out. We'll reply within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 border border-zinc-200 px-5 py-2.5 transition-colors"
                    >
                      Send Another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">

                    {/* Name + Email side by side on md+ */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                          Your Name
                        </label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={update("name")}
                          required
                          placeholder="Full name"
                          className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors bg-[#FAFAFA]"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={update("email")}
                          required
                          placeholder="your@email.com"
                          className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors bg-[#FAFAFA]"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                        Subject
                      </label>
                      <select
                        value={form.subject}
                        onChange={update("subject")}
                        required
                        className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors bg-[#FAFAFA] text-zinc-700"
                        data-testid="contact-subject-select"
                      >
                        <option value="">Select a topic…</option>
                        <option value="Order Query">Order Query</option>
                        <option value="Return / Exchange">Return / Exchange</option>
                        <option value="Product Question">Product Question</option>
                        <option value="Bulk / Wholesale">Bulk / Wholesale Enquiry</option>
                        <option value="Feedback">Feedback</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                        Message
                      </label>
                      <textarea
                        value={form.message}
                        onChange={update("message")}
                        required
                        rows={5}
                        placeholder="Tell us how we can help you…"
                        className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors bg-[#FAFAFA] resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-3.5 uppercase tracking-widest text-xs transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <Send size={13} />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ SECTION ───────────────────────────────────────────────── */}
      <section
        id="faq"
        ref={faqRef}
        className="py-16 sm:py-24 bg-white section-hidden"
        data-testid="faq-section"
      >
        <div className="px-6 md:px-12 max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
              Quick Answers
            </p>
            <h2 className="cormorant text-4xl sm:text-5xl font-light text-zinc-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="bg-[#FAFAFA] border border-zinc-100 px-6 py-2">
            {FAQS.map((faq, idx) => (
              <FAQItem key={idx} faq={faq} index={idx} />
            ))}
          </div>

          {/* Still have questions */}
          <div className="text-center mt-10">
            <p className="text-sm text-zinc-500 mb-2">
              Can't find what you're looking for?
            </p>
            <p className="text-xs text-zinc-400 mb-5">Our support team is happy to help.</p>
            <a
              href={`https://wa.me/${contactInfo.whatsapp}?text=Hi%20Rivora!%20I%20have%20a%20question.`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#1ebe57] transition-colors"
            >
              <MessageCircle size={14} />
              Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}